/**
 * Filters consolidated donations based on search criteria
 * @param {Array} consolidatedDonations - List of consolidated donations
 * @param {Object} searchFilters - Search filters object
 * @returns {Array} Filtered consolidated donations
 */
export const filterConsolidatedDonations = (
  consolidatedDonations,
  searchFilters
) => {
  // Safety check
  if (
    !Array.isArray(consolidatedDonations) ||
    consolidatedDonations.length === 0
  ) {
    return [];
  }

  // If no filters applied, return all records
  if (
    !searchFilters.donorId &&
    !searchFilters.bookNo &&
    !searchFilters.remarks &&
    !searchFilters.receiptNo &&
    !searchFilters.globalSearch
  ) {
    return consolidatedDonations;
  }

  const filtered = consolidatedDonations.filter((consolidated) => {
    const parent = consolidated?.parentDetails;
    if (!parent) {
      return false;
    }

    // Apply EXACT MATCH filters
    if (
      searchFilters.donorId &&
      parent.donorId?.toString() !== searchFilters.donorId
    ) {
      return false;
    }
    if (
      searchFilters.bookNo &&
      parent.bookNo?.toString() !== searchFilters.bookNo
    ) {
      return false;
    }
    if (
      searchFilters.receiptNo &&
      consolidated.receiptNo?.toString() !== searchFilters.receiptNo
    ) {
      return false;
    }
    if (
      searchFilters.remarks &&
      parent.donationRemarks?.toString().toLowerCase() !==
        searchFilters.remarks.toLowerCase()
    ) {
      return false;
    }
    // Global search uses partial match for better UX
    if (searchFilters.globalSearch) {
      const search = searchFilters.globalSearch.toLowerCase();
      return (
        parent.donorId?.toString().toLowerCase().includes(search) ||
        parent.firstName?.toLowerCase().includes(search) ||
        parent.lastName?.toLowerCase().includes(search) ||
        parent.mobile1?.toString().includes(search) ||
        parent.city?.toLowerCase().includes(search) ||
        consolidated.receiptNo?.toString().toLowerCase().includes(search)
      );
    }
    return true;
  });

  return filtered;
};

/**
 * Groups filtered consolidated donations by donor
 * @param {Array} filteredConsolidatedDonations - Filtered consolidated donations
 * @returns {Array} Grouped donations by donor
 */
export const groupDonationsByDonor = (filteredConsolidatedDonations) => {
  const donorMap = new Map();

  filteredConsolidatedDonations.forEach((consolidated) => {
    const donorId = consolidated.parentDetails?.donorId;
    if (!donorId) return;

    if (!donorMap.has(donorId)) {
      donorMap.set(donorId, {
        donorId: donorId,
        donorDetails: consolidated.parentDetails,
        receipts: [],
      });
    }

    donorMap.get(donorId).receipts.push(consolidated);
  });

  return Array.from(donorMap.values());
};

/**
 * Calculates total individual donations count
 * @param {Array} consolidatedDonations - List of consolidated donations
 * @returns {number} Total donation count
 */
export const calculateTotalDonations = (consolidatedDonations) => {
  if (!Array.isArray(consolidatedDonations)) return 0;

  // Use donationCount from backend (already includes parent + children)
  return consolidatedDonations.reduce((sum, consolidated) => {
    return sum + (consolidated.donationCount || 0);
  }, 0);
};

/**
 * Consolidates all data processing into a single function
 * @param {Array} consolidatedDonations - List of consolidated donations
 * @param {Object} searchFilters - Search filters object
 * @returns {Object} Object containing filtered, grouped donations and total count
 */
export const consolidateDataHandler = (
  consolidatedDonations,
  searchFilters
) => {
  const filteredConsolidatedDonations = filterConsolidatedDonations(
    consolidatedDonations,
    searchFilters
  );

  const groupedByDonor = groupDonationsByDonor(
    filteredConsolidatedDonations
  );

  const totalIndividualDonations = calculateTotalDonations(
    consolidatedDonations
  );

  return {
    filteredConsolidatedDonations,
    groupedByDonor,
    totalIndividualDonations,
  };
};
